import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Package, CreditCard, ArrowLeft, Sparkles, ShoppingCart,
  ChevronDown, ChevronUp, Clock, Star, Eye, X,
  Lock, CheckCircle, BookOpen, Users, BarChart3,
  Check, Square, CheckSquare, Unlock, Gift,
} from 'lucide-react';
import { bundlesApi, methodPurchaseApi, methodsApi } from '../services/api';
import { useToast } from '../context/ToastContext';
import './Pages.css';

interface Bundle {
  id: string;
  name: string;
  description: string | null;
  bundle_size: number;
  price_eur: number;
  is_all_access: boolean;
}

interface PurchaseInfo {
  id: string;
  bundle_name: string;
  credits_total: number;
  credits_used: number;
  amount_eur: number;
  paid_at: string | null;
}

interface Credits {
  total_credits: number;
  used_credits: number;
  available_credits: number;
  has_all_access: boolean;
  purchases: PurchaseInfo[];
}

interface MethodStep { step: number; text: string; }

interface PurchasableMethod {
  id: string;
  name: string;
  description: string | null;
  benefit: string | null;
  instructions: MethodStep[] | null;
  debriefing: string | null;
  materials: string | null;
  theory_background: string | null;
  category_id: string | null;
  duration_minutes: number | null;
  duration_max_minutes: number | null;
  min_participants: number | null;
  max_participants: number | null;
  difficulty_level: string;
  is_purchasable: boolean;
  is_starter: boolean;
  price_eur: number | null;
}

const difficultyLabels: Record<string, string> = {
  beginner: 'Einsteiger', intermediate: 'Fortgeschritten', advanced: 'Experte',
};
const difficultyColors: Record<string, string> = {
  beginner: 'var(--success)', intermediate: 'var(--accent)', advanced: 'var(--danger)',
};

function formatDuration(min: number | null, max: number | null): string {
  if (!min) return '';
  if (max && max !== min) return `${min}–${max} Min.`;
  return `${min} Min.`;
}
function formatParticipants(min: number | null, max: number | null): string {
  if (!min && !max) return '';
  if (!max) return `ab ${min} Pers.`;
  if (min === max) return `${min} Pers.`;
  return `${min ?? 1}–${max} Pers.`;
}

export default function MethodShop() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [credits, setCredits] = useState<Credits | null>(null);
  const [loading, setLoading] = useState(true);
  const [buyingId, setBuyingId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  // Methoden-Vorschau
  const [methods, setMethods] = useState<PurchasableMethod[]>([]);
  const [unlockedIds, setUnlockedIds] = useState<Set<string>>(new Set());
  const [previewMethod, setPreviewMethod] = useState<PurchasableMethod | null>(null);

  // Warenkorb (ausgewählte Methoden)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [unlocking, setUnlocking] = useState(false);
  const [methodsExpanded, setMethodsExpanded] = useState(false);

  // Promo-Code
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);

  // Rückkehr von Stripe prüfen
  useEffect(() => {
    const payment = searchParams.get('payment');
    if (payment === 'success') {
      showToast('Zahlung erfolgreich! Deine Credits wurden gutgeschrieben.', 'success');
      window.history.replaceState({}, '', '/methoden/shop');
    } else if (payment === 'cancelled') {
      showToast('Zahlung abgebrochen.', 'info');
      window.history.replaceState({}, '', '/methoden/shop');
    }
  }, []); // eslint-disable-line

  // Daten laden
  useEffect(() => {
    const loadData = async () => {
      try {
        const bundleRes = await bundlesApi.list();
        const raw = bundleRes.data?.items ?? bundleRes.data ?? [];
        setBundles(raw.map((b: any) => ({
          ...b,
          price_eur: Number(b.price_eur) || 0,
          is_all_access: b.is_all_access ?? false,
        })));
      } catch {
        showToast('Bundles konnten nicht geladen werden', 'error');
      }
      try {
        const creditRes = await methodPurchaseApi.getCredits();
        setCredits(creditRes.data);
      } catch { /* ignorieren */ }
      try {
        const methodRes = await methodsApi.list({ sort: 'name_asc' });
        const allMethods = methodRes.data?.items ?? [];
        setMethods(allMethods.filter((m: any) => m.is_purchasable || m.is_starter));
      } catch { /* ignorieren */ }
      try {
        const unlockRes = await methodPurchaseApi.getUnlocked();
        setUnlockedIds(new Set(unlockRes.data.unlocked_method_ids));
      } catch { /* ignorieren */ }
      setLoading(false);
    };
    loadData();
  }, []); // eslint-disable-line

  const handleBuy = async (bundleId: string) => {
    setBuyingId(bundleId);
    try {
      const res = await methodPurchaseApi.createCheckout(bundleId);
      window.location.href = res.data.checkout_url;
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Checkout konnte nicht gestartet werden';
      showToast(detail, 'error');
      setBuyingId(null);
    }
  };

  const handleRedeemCode = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const res = await methodPurchaseApi.redeemCode(promoCode.trim());
      showToast(res.data.message || 'Promo-Code eingelöst!', 'success');
      setPromoCode('');
      // Reload credits
      const creditRes = await methodPurchaseApi.getCredits();
      setCredits(creditRes.data);
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Promo-Code konnte nicht eingelöst werden';
      showToast(detail, 'error');
    } finally {
      setPromoLoading(false);
    }
  };

  const openPreview = (m: PurchasableMethod) => {
    setPreviewMethod(m);
    methodsApi.get(m.id).then((res) => setPreviewMethod(res.data)).catch(() => {});
  };

  // Methode auswählen / abwählen
  const toggleSelect = (methodId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(Array.from(prev));
      if (next.has(methodId)) {
        next.delete(methodId);
      } else {
        next.add(methodId);
      }
      return next;
    });
  };

  // Alle auswählen / abwählen
  const selectableMethods = methods.filter((m) => !unlockedIds.has(m.id) && !m.is_starter);
  const allSelected = selectableMethods.length > 0 && selectableMethods.every((m) => selectedIds.has(m.id));

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableMethods.map((m) => m.id)));
    }
  };

  // Passendes Bundle empfehlen
  const selectedCount = selectedIds.size;
  const availableCredits = credits?.available_credits ?? 0;
  const creditsNeeded = Math.max(0, selectedCount - availableCredits);

  const recommendedBundle = useMemo(() => {
    if (creditsNeeded <= 0) return null;
    // Sortiere Bundles nach Größe aufsteigend, finde kleinstes das reicht
    const sorted = [...bundles]
      .filter((b) => !b.is_all_access)
      .sort((a, b) => a.bundle_size - b.bundle_size);
    const exact = sorted.find((b) => b.bundle_size >= creditsNeeded);
    if (exact) return exact;
    // Kein Bundle reicht → empfehle das größte (oder Komplett)
    const allAccess = bundles.find((b) => b.is_all_access);
    if (allAccess) return allAccess;
    return sorted[sorted.length - 1] ?? null;
  }, [bundles, creditsNeeded]);

  // Ausgewählte Methoden freischalten (wenn genug Credits)
  const handleUnlockSelected = async () => {
    if (selectedCount === 0) return;
    setUnlocking(true);
    let successCount = 0;
    let failCount = 0;
    for (const methodId of Array.from(selectedIds)) {
      try {
        await methodPurchaseApi.unlock(methodId);
        successCount++;
      } catch {
        failCount++;
      }
    }
    // Daten neu laden
    try {
      const unlockRes = await methodPurchaseApi.getUnlocked();
      setUnlockedIds(new Set(unlockRes.data.unlocked_method_ids));
    } catch {}
    try {
      const creditRes = await methodPurchaseApi.getCredits();
      setCredits(creditRes.data);
    } catch {}
    setSelectedIds(new Set());
    setUnlocking(false);
    if (successCount > 0) {
      showToast(`${successCount} Methode${successCount > 1 ? 'n' : ''} freigeschaltet!`, 'success');
    }
    if (failCount > 0) {
      showToast(`${failCount} Methode${failCount > 1 ? 'n' : ''} konnten nicht freigeschaltet werden.`, 'error');
    }
  };

  const pricePerMethod = (bundle: Bundle) => {
    if (bundle.bundle_size <= 0) return 0;
    return bundle.price_eur / bundle.bundle_size;
  };

  const purchasableCount = methods.length;
  const unlockedCount = methods.filter((m) => unlockedIds.has(m.id)).length;

  return (
    <div style={{ paddingBottom: selectedCount > 0 ? 100 : 0 }}>
      {/* Page Header */}
      <div className="page-header mb-24">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => navigate('/methoden')}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <ArrowLeft size={16} />
            Zurück
          </button>
          <div>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ShoppingCart size={24} />
              Methoden-Shop
            </h1>
            <p className="text-muted text-sm">Methoden auswählen, Credits kaufen, freischalten</p>
          </div>
        </div>
      </div>

      {/* Credit-Balance */}
      {credits && credits.total_credits > 0 && (
        <div className="card" style={{
          marginBottom: 24,
          background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark, #8a6050) 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 12,
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <CreditCard size={24} />
            </div>
            <div>
              <div style={{ fontSize: '0.85rem', opacity: 0.85 }}>Verfügbare Credits</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, lineHeight: 1.1 }}>
                {credits.available_credits}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 24, fontSize: '0.85rem', opacity: 0.85 }}>
            <div>
              <div>Gesamt</div>
              <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{credits.total_credits}</div>
            </div>
            <div>
              <div>Verwendet</div>
              <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{credits.used_credits}</div>
            </div>
            {credits.has_all_access && (
              <div style={{
                background: 'rgba(255,255,255,0.2)', borderRadius: 8,
                padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600,
              }}>
                <Sparkles size={16} /> Komplett-Zugang
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ SCHRITT 1: Methoden auswählen (ausklappbar) ═══ */}
      {!loading && methods.length > 0 && (
        <div className="card" style={{ marginBottom: 32, padding: 0, overflow: 'hidden' }}>
          {/* Klappbarer Header */}
          <button
            onClick={() => setMethodsExpanded(!methodsExpanded)}
            style={{
              width: '100%', padding: '16px 20px',
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ShoppingCart size={18} style={{ color: 'var(--primary)' }} />
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text)' }}>
                  Schritt 1: Methoden auswählen
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 2 }}>
                  {purchasableCount} verfügbar
                  {unlockedCount > 0 && ` · ${unlockedCount} freigeschaltet`}
                  {selectedCount > 0 && (
                    <span style={{ color: 'var(--primary)', fontWeight: 600 }}>
                      {' '}· {selectedCount} ausgewählt
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {selectedCount > 0 && (
                <span style={{
                  background: 'var(--primary)', color: '#fff',
                  width: 26, height: 26, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: '0.85rem',
                }}>
                  {selectedCount}
                </span>
              )}
              {methodsExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          </button>

          {/* Ausgeklappter Inhalt */}
          {methodsExpanded && (
            <div style={{ padding: '0 20px 20px' }}>
              {/* Toolbar */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: 12, paddingBottom: 12,
                borderBottom: '1px solid var(--border-light)',
              }}>
                <p className="text-muted text-sm" style={{ margin: 0 }}>
                  Klicke auf Methoden, die du freischalten möchtest
                </p>
                {selectableMethods.length > 0 && (
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={toggleAll}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem' }}
                  >
                    {allSelected ? <CheckSquare size={14} /> : <Square size={14} />}
                    {allSelected ? 'Keine' : 'Alle'} auswählen
                  </button>
                )}
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: 12,
              }}>
                {methods.map((m) => {
                  const isStarter = m.is_starter;
                  const isUnlocked = unlockedIds.has(m.id);
                  const isFree = isStarter || isUnlocked;
                  const isSelected = selectedIds.has(m.id);
                  return (
                    <div
                      key={m.id}
                      className="card"
                      onClick={() => {
                        if (!isFree) toggleSelect(m.id);
                      }}
                      style={{
                        cursor: isFree ? 'default' : 'pointer',
                        padding: '14px 16px',
                        transition: 'all 0.15s',
                        position: 'relative',
                        borderLeft: `3px solid ${isFree ? 'var(--success)' : isSelected ? 'var(--primary)' : 'var(--border)'}`,
                        background: isSelected ? 'rgba(176,116,104,0.06)' : undefined,
                        opacity: isFree ? 0.7 : 1,
                      }}
                    >
                      {/* Auswahl-Checkbox (oben links) */}
                      <div style={{
                        position: 'absolute', top: 8, left: -2,
                        width: 24, height: 24,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {isFree ? (
                          <CheckCircle size={18} style={{ color: 'var(--success)' }} />
                        ) : isSelected ? (
                          <CheckSquare size={18} style={{ color: 'var(--primary)' }} />
                        ) : (
                          <Square size={18} style={{ color: 'var(--text-muted)', opacity: 0.4 }} />
                        )}
                      </div>

                      {/* Vorschau-Button (oben rechts) */}
                      <button
                        onClick={(e) => { e.stopPropagation(); openPreview(m); }}
                        style={{
                          position: 'absolute', top: 8, right: 8,
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--primary)', padding: 2, borderRadius: 4,
                          display: 'flex', alignItems: 'center', gap: 3,
                          fontSize: '0.7rem', fontWeight: 600,
                        }}
                        title="Vorschau"
                      >
                        <Eye size={14} />
                      </button>

                      <div style={{
                        fontWeight: 600, fontSize: '0.9rem',
                        paddingLeft: 20, paddingRight: 28,
                        marginBottom: 4,
                      }}>
                        {m.name}
                      </div>
                      {m.description && (
                        <p className="text-muted" style={{
                          fontSize: '0.78rem', margin: 0, lineHeight: 1.4,
                          paddingLeft: 20,
                          display: '-webkit-box', WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        }}>
                          {m.description}
                        </p>
                      )}
                      <div style={{
                        display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap',
                        paddingLeft: 20,
                      }}>
                        {m.duration_minutes && (
                          <span style={{
                            fontSize: '0.7rem', color: 'var(--text-muted)',
                            display: 'flex', alignItems: 'center', gap: 3,
                          }}>
                            <Clock size={11} /> {formatDuration(m.duration_minutes, m.duration_max_minutes)}
                          </span>
                        )}
                        <span style={{
                          fontSize: '0.7rem',
                          display: 'flex', alignItems: 'center', gap: 3,
                          color: difficultyColors[m.difficulty_level],
                        }}>
                          <BarChart3 size={11} /> {difficultyLabels[m.difficulty_level]}
                        </span>
                      </div>

                      {/* Status-Label */}
                      {isStarter && (
                        <div style={{
                          marginTop: 8, paddingLeft: 20,
                          fontSize: '0.72rem', color: 'var(--success)',
                          fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
                        }}>
                          <CheckCircle size={12} /> Inklusive — Starter-Methode
                        </div>
                      )}
                      {!isStarter && isUnlocked && (
                        <div style={{
                          marginTop: 8, paddingLeft: 20,
                          fontSize: '0.72rem', color: 'var(--success)',
                          fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
                        }}>
                          <Check size={12} /> Bereits freigeschaltet
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{
          textAlign: 'center', padding: '64px 24px',
          color: 'var(--text-muted)', fontSize: '0.9rem',
        }}>
          Wird geladen...
        </div>
      )}

      {/* ═══ SCHRITT 2: Credits kaufen ═══ */}
      {!loading && bundles.length > 0 && (
        <div id="bundles-section" style={{ marginBottom: 32 }}>
          <div style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: '1.1rem', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <CreditCard size={18} style={{ color: 'var(--primary)' }} />
              Schritt 2: Credits kaufen
            </h2>
            <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>
              1 Credit = 1 Methode freischalten · Einmalzahlung, kein Abo
              {selectedCount > 0 && creditsNeeded > 0 && (
                <span style={{ color: 'var(--primary)', fontWeight: 600 }}>
                  {' '}· Du brauchst noch {creditsNeeded} Credit{creditsNeeded !== 1 ? 's' : ''}
                </span>
              )}
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 20,
          }}>
            {bundles.map((bundle) => {
              const isRecommended = recommendedBundle?.id === bundle.id && creditsNeeded > 0;
              return (
                <div
                  key={bundle.id}
                  className="card"
                  style={{
                    position: 'relative',
                    display: 'flex', flexDirection: 'column',
                    overflow: 'hidden',
                    border: isRecommended ? '2px solid var(--primary)' : undefined,
                    boxShadow: isRecommended ? '0 0 0 3px rgba(176,116,104,0.15)' : undefined,
                  }}
                >
                  {/* Empfohlen-Badge */}
                  {isRecommended && (
                    <div style={{
                      position: 'absolute', top: -1, left: -1, right: -1,
                      background: 'var(--primary)', color: '#fff',
                      fontSize: '0.72rem', fontWeight: 700,
                      padding: '4px 12px', textAlign: 'center',
                      borderRadius: '4px 4px 0 0',
                      textTransform: 'uppercase', letterSpacing: '0.5px',
                    }}>
                      Empfohlen für deine Auswahl ({selectedCount} Methoden)
                    </div>
                  )}

                  {/* Einmalzahlung Badge */}
                  <div style={{
                    position: 'absolute', top: isRecommended ? 36 : 12, right: 12,
                    background: 'var(--success)', color: '#fff',
                    fontSize: '0.7rem', fontWeight: 700,
                    padding: '3px 10px', borderRadius: 20,
                    textTransform: 'uppercase', letterSpacing: '0.5px',
                  }}>
                    Einmalzahlung
                  </div>

                  {/* All-Access Badge */}
                  {bundle.is_all_access && (
                    <div style={{
                      position: 'absolute', top: isRecommended ? 36 : 12, left: 12,
                      background: 'var(--accent, #e6a817)', color: '#fff',
                      fontSize: '0.7rem', fontWeight: 700,
                      padding: '3px 10px', borderRadius: 20,
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                      <Star size={12} /> Komplett
                    </div>
                  )}

                  {/* Bundle Icon + Name */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    marginBottom: 8,
                    marginTop: isRecommended
                      ? (bundle.is_all_access ? 52 : 44)
                      : (bundle.is_all_access ? 28 : 20),
                    paddingRight: 100,
                  }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: bundle.is_all_access ? 'var(--accent, #e6a817)' : 'var(--primary)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Package size={20} color="#fff" />
                    </div>
                    <h3 style={{ fontSize: '1.05rem', margin: 0 }}>{bundle.name}</h3>
                  </div>

                  {/* Description */}
                  {bundle.description && (
                    <p className="text-light text-sm" style={{
                      display: '-webkit-box', WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      lineHeight: 1.5, marginBottom: 16,
                    }}>
                      {bundle.description}
                    </p>
                  )}

                  {/* Value Proposition */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 16,
                    padding: '10px 0',
                    borderTop: '1px solid var(--border-light)',
                    marginTop: 'auto', marginBottom: 12,
                  }}>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                      {bundle.bundle_size} {bundle.is_all_access ? 'Credits + alle Methoden' : 'Methoden-Credits'}
                    </div>
                    {!bundle.is_all_access && (
                      <div style={{ fontSize: '0.8rem', color: 'var(--success)', fontWeight: 600 }}>
                        nur {pricePerMethod(bundle).toFixed(2).replace('.', ',')} EUR/Methode
                      </div>
                    )}
                  </div>

                  {/* Price + Buy Button */}
                  <div style={{
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', gap: 12,
                  }}>
                    <span style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--primary)' }}>
                      {bundle.price_eur.toFixed(2).replace('.', ',')} EUR
                    </span>
                    <button
                      className="btn btn-primary"
                      disabled={buyingId === bundle.id}
                      onClick={() => handleBuy(bundle.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        whiteSpace: 'nowrap',
                        opacity: buyingId === bundle.id ? 0.6 : 1,
                      }}
                    >
                      {buyingId === bundle.id ? (
                        <>Weiterleitung...</>
                      ) : (
                        <><CreditCard size={16} /> Jetzt kaufen</>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ═══ PROMO-CODE ═══ */}
      {!loading && (
        <div className="card" style={{ padding: 20, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Gift size={18} style={{ color: 'var(--primary)' }} />
            <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Promo-Code einlösen</h3>
          </div>
          <p className="text-muted text-sm" style={{ margin: '0 0 12px' }}>
            Du hast einen Promo-Code? Gib ihn hier ein, um kostenlose Credits zu erhalten.
          </p>
          <div style={{ display: 'flex', gap: 8, maxWidth: 400 }}>
            <input
              type="text"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              placeholder="z.B. BETA2026"
              style={{ flex: 1, textTransform: 'uppercase' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && promoCode.trim()) {
                  e.preventDefault();
                  handleRedeemCode();
                }
              }}
            />
            <button
              className="btn btn-primary"
              disabled={!promoCode.trim() || promoLoading}
              onClick={handleRedeemCode}
            >
              {promoLoading ? 'Wird eingelöst...' : 'Einlösen'}
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && bundles.length === 0 && methods.length === 0 && (
        <div className="empty-state card">
          <Package size={40} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
          <p>Aktuell sind keine Bundles verfügbar.</p>
        </div>
      )}

      {/* Kaufhistorie */}
      {credits && credits.purchases.length > 0 && (
        <div className="card" style={{ padding: 0 }}>
          <button
            onClick={() => setShowHistory(!showHistory)}
            style={{
              width: '100%', padding: '14px 20px',
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              fontSize: '0.9rem', fontWeight: 600, color: 'var(--text)',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Clock size={16} style={{ color: 'var(--primary)' }} />
              Kaufhistorie ({credits.purchases.length})
            </span>
            {showHistory ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          {showHistory && (
            <div style={{ padding: '0 20px 16px' }}>
              {credits.purchases.map((p) => (
                <div key={p.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 0', borderBottom: '1px solid var(--border-light)',
                  fontSize: '0.85rem',
                }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{p.bundle_name}</div>
                    <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                      {p.paid_at ? new Date(p.paid_at).toLocaleDateString('de-DE') : 'Ausstehend'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 600, color: 'var(--primary)' }}>
                      {Number(p.amount_eur).toFixed(2).replace('.', ',')} EUR
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                      {p.credits_used}/{p.credits_total} Credits verwendet
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ STICKY WARENKORB-LEISTE (unten) ═══ */}
      {selectedCount > 0 && (
        <div style={{
          position: 'fixed',
          bottom: 0, left: 0, right: 0,
          background: 'var(--bg, #fff)',
          borderTop: '2px solid var(--primary)',
          padding: '12px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 12,
          zIndex: 100,
          boxShadow: '0 -4px 20px rgba(0,0,0,0.12)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'var(--primary)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, fontSize: '1rem',
            }}>
              {selectedCount}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                {selectedCount} Methode{selectedCount !== 1 ? 'n' : ''} ausgewählt
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                {creditsNeeded > 0 ? (
                  <>Noch {creditsNeeded} Credit{creditsNeeded !== 1 ? 's' : ''} nötig — kaufe ein Bundle oben</>
                ) : (
                  <>{availableCredits} Credit{availableCredits !== 1 ? 's' : ''} verfügbar — bereit zum Freischalten!</>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => setSelectedIds(new Set())}
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <X size={14} /> Leeren
            </button>
            {creditsNeeded <= 0 && (
              <button
                className="btn btn-primary"
                onClick={handleUnlockSelected}
                disabled={unlocking}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  opacity: unlocking ? 0.6 : 1,
                }}
              >
                {unlocking ? (
                  <>Wird freigeschaltet...</>
                ) : (
                  <><Unlock size={16} /> {selectedCount} Methode{selectedCount !== 1 ? 'n' : ''} freischalten</>
                )}
              </button>
            )}
            {creditsNeeded > 0 && recommendedBundle && (
              <button
                className="btn btn-primary"
                onClick={() => {
                  document.getElementById('bundles-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <CreditCard size={16} /> Credits kaufen
              </button>
            )}
          </div>
        </div>
      )}

      {/* ═══ Methoden-Vorschau Modal ═══ */}
      {previewMethod && (
        <div className="modal-overlay" onClick={() => setPreviewMethod(null)}>
          <div
            className="md-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 640 }}
          >
            <div style={{
              height: 4, borderRadius: '4px 4px 0 0',
              background: unlockedIds.has(previewMethod.id)
                ? 'var(--success)'
                : selectedIds.has(previewMethod.id)
                  ? 'var(--primary)'
                  : 'var(--border)',
            }} />

            {/* Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              padding: '20px 24px 0',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 style={{ margin: '0 0 6px', fontSize: '1.15rem' }}>{previewMethod.name}</h2>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {previewMethod.duration_minutes && (
                    <span className="meta-badge" style={{ fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Clock size={13} /> {formatDuration(previewMethod.duration_minutes, previewMethod.duration_max_minutes)}
                    </span>
                  )}
                  {(previewMethod.min_participants || previewMethod.max_participants) && (
                    <span className="meta-badge" style={{ fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Users size={13} /> {formatParticipants(previewMethod.min_participants, previewMethod.max_participants)}
                    </span>
                  )}
                  <span className="meta-badge" style={{
                    fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 4,
                    color: difficultyColors[previewMethod.difficulty_level],
                  }}>
                    <BarChart3 size={13} /> {difficultyLabels[previewMethod.difficulty_level]}
                  </span>
                </div>
              </div>
              <button className="btn-icon" onClick={() => setPreviewMethod(null)}>
                <X size={20} />
              </button>
            </div>

            {/* Status-Banner + Auswahl-Button */}
            <div style={{
              margin: '16px 24px',
              padding: '10px 14px',
              borderRadius: 8,
              background: (previewMethod as any).is_starter || unlockedIds.has(previewMethod.id)
                ? 'rgba(91,140,90,0.08)'
                : selectedIds.has(previewMethod.id)
                  ? 'rgba(176,116,104,0.08)'
                  : 'rgba(176,116,104,0.03)',
              border: `1px solid ${(previewMethod as any).is_starter || unlockedIds.has(previewMethod.id) ? 'var(--success)' : selectedIds.has(previewMethod.id) ? 'var(--primary)' : 'var(--border)'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 8, fontSize: '0.85rem',
            }}>
              {(previewMethod as any).is_starter ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircle size={16} style={{ color: 'var(--success)' }} />
                  <span style={{ fontWeight: 600, color: 'var(--success)' }}>Starter-Methode — in deinem Plan enthalten</span>
                </div>
              ) : unlockedIds.has(previewMethod.id) ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircle size={16} style={{ color: 'var(--success)' }} />
                  <span style={{ fontWeight: 600, color: 'var(--success)' }}>Freigeschaltet</span>
                </div>
              ) : selectedIds.has(previewMethod.id) ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CheckSquare size={16} style={{ color: 'var(--primary)' }} />
                    <span style={{ fontWeight: 600, color: 'var(--primary)' }}>Ausgewählt</span>
                  </div>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => toggleSelect(previewMethod.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    <X size={12} /> Abwählen
                  </button>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Lock size={16} style={{ color: 'var(--text-muted)' }} />
                    <span>Premium-Methode — 1 Credit</span>
                  </div>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => toggleSelect(previewMethod.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    <ShoppingCart size={12} /> Auswählen
                  </button>
                </>
              )}
            </div>

            {/* Content */}
            <div style={{ padding: '0 24px 24px', maxHeight: '60vh', overflowY: 'auto' }}>
              {previewMethod.description && (
                <p style={{ lineHeight: 1.6, marginBottom: 16 }}>{previewMethod.description}</p>
              )}

              {previewMethod.benefit && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ fontSize: '0.9rem', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <BookOpen size={15} style={{ color: 'var(--primary)' }} /> Nutzen & Einsatzgebiet
                  </h4>
                  <p style={{ fontSize: '0.88rem', lineHeight: 1.6, color: 'var(--text-light)' }}>
                    {previewMethod.benefit}
                  </p>
                </div>
              )}

              {previewMethod.instructions && previewMethod.instructions.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ fontSize: '0.9rem', marginBottom: 6 }}>Ablauf</h4>
                  <ol style={{ paddingLeft: 20, margin: 0, fontSize: '0.88rem', lineHeight: 1.7 }}>
                    {previewMethod.instructions.map((s) => (
                      <li key={s.step} style={{ marginBottom: 4 }}>{s.text}</li>
                    ))}
                  </ol>
                </div>
              )}

              {previewMethod.materials && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ fontSize: '0.9rem', marginBottom: 6 }}>Materialien</h4>
                  <p style={{ fontSize: '0.88rem', lineHeight: 1.6, color: 'var(--text-light)' }}>
                    {previewMethod.materials}
                  </p>
                </div>
              )}

              {previewMethod.debriefing && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ fontSize: '0.9rem', marginBottom: 6 }}>Nachbesprechung</h4>
                  <p style={{ fontSize: '0.88rem', lineHeight: 1.6, color: 'var(--text-light)' }}>
                    {previewMethod.debriefing}
                  </p>
                </div>
              )}

              {previewMethod.theory_background && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ fontSize: '0.9rem', marginBottom: 6 }}>Theoriehintergrund</h4>
                  <p style={{ fontSize: '0.88rem', lineHeight: 1.6, color: 'var(--text-light)' }}>
                    {previewMethod.theory_background}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
